import { useEffect, useState } from "react";
import { Loader2, Star, Trash2 } from "lucide-react";
import { Switch } from "@/components/ui/switch";
import { toast } from "sonner";
import { API, authHeaders, fmtDate } from "./adminApi";

const resolveUrl = (u) => (u.startsWith("/") ? `${API}${u}` : u);

export const ReviewsTab = () => {
  const [reviews, setReviews] = useState(null);

  const fetchReviews = () => {
    fetch(`${API}/api/admin/reviews`, { headers: authHeaders() })
      .then((r) => (r.ok ? r.json() : []))
      .then(setReviews)
      .catch(() => setReviews([]));
  };

  useEffect(fetchReviews, []);

  const moderate = async (review, changes) => {
    const res = await fetch(`${API}/api/admin/reviews/${review.id}`, {
      method: "PUT",
      headers: { "Content-Type": "application/json", ...authHeaders() },
      body: JSON.stringify({ approved: review.approved, featured: review.featured, ...changes }),
    });
    if (res.ok) fetchReviews();
    else toast.error("Could not update review");
  };

  const remove = async (review) => {
    if (!window.confirm(`Delete the review from ${review.name}?`)) return;
    const res = await fetch(`${API}/api/admin/reviews/${review.id}`, { method: "DELETE", headers: authHeaders() });
    if (res.ok) {
      toast.success("Review deleted");
      fetchReviews();
    } else {
      toast.error("Could not delete review");
    }
  };

  if (reviews === null) return <div className="py-16 flex justify-center"><Loader2 className="animate-spin text-primary" size={28} /></div>;

  const pending = reviews.filter((r) => !r.approved).length;

  return (
    <div data-testid="admin-reviews-tab">
      <p className="font-body text-sm text-muted-foreground mb-5">
        {reviews.length} reviews{pending ? ` · ${pending} awaiting approval` : ""} · Featured reviews appear on the homepage
      </p>
      <div className="space-y-3">
        {reviews.map((r) => (
          <div key={r.id} data-testid={`review-row-${r.id}`} className={`rounded-lg border p-4 ${r.approved ? "border-border/60 bg-white" : "border-accent/50 bg-accent/5"}`}>
            <div className="flex flex-wrap items-center gap-3 mb-2">
              <span className="font-heading text-sm font-semibold text-foreground">{r.name}</span>
              <span className="flex items-center gap-0.5">
                {[...Array(r.rating)].map((_, j) => <Star key={j} size={12} className="fill-accent text-accent" />)}
              </span>
              <span className="font-body text-xs text-muted-foreground">
                {[r.location, r.type, fmtDate(r.created_at)].filter(Boolean).join(" · ")}
              </span>
              {!r.approved && (
                <span className="font-body text-[10px] uppercase tracking-wider bg-accent text-white px-2 py-0.5 rounded-full">Pending</span>
              )}
              <div className="ml-auto flex items-center gap-4">
                <label className="flex items-center gap-1.5 font-body text-xs text-muted-foreground">
                  Approved
                  <Switch data-testid={`review-approve-${r.id}`} checked={Boolean(r.approved)} onCheckedChange={(v) => moderate(r, { approved: v, featured: v && r.featured })} />
                </label>
                <label className="flex items-center gap-1.5 font-body text-xs text-muted-foreground">
                  Featured
                  <Switch data-testid={`review-feature-${r.id}`} checked={Boolean(r.featured)} disabled={!r.approved} onCheckedChange={(v) => moderate(r, { featured: v })} />
                </label>
                <button data-testid={`review-delete-${r.id}`} onClick={() => remove(r)} className="text-muted-foreground hover:text-destructive p-1" aria-label="Delete">
                  <Trash2 size={15} />
                </button>
              </div>
            </div>
            <p className="font-body text-sm text-foreground/80 italic">&ldquo;{r.text}&rdquo;</p>
            {r.photos?.length > 0 && (
              <div className="flex gap-2 mt-2">
                {r.photos.map((p) => (
                  <img key={p} src={resolveUrl(p)} alt="Review" className="h-14 w-18 object-cover rounded border border-border/60" />
                ))}
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
};
